import classnames from 'classnames';
import React, { useState } from 'react';
import { Badge } from 'react-bootstrap';
import { connect } from 'react-redux';
import {
  NavLink,
  Redirect,
  Route,
  Switch,
  useRouteMatch,
} from 'react-router-dom';
import BackgroundImages, {
  BG_IMAGES,
} from 'v2/common/Background/BackgroundImages';
import Count from 'v2/common/Count/Count';
import { useNotificationContext } from 'v2/context/notificationContext';
import { GenericObject, ProposalEntity } from 'v2/models';
import {
  isApprovedProposal,
  isClosedApprovedProposal,
  isClosedCancelledProposal,
  isClosedDeclinedProposal,
  isClosedRejectedProposal,
  isWaitingBuyerProposal,
  isWaitingForSellerProposal,
} from 'v2/utils/proposalUtils';
import { MOBILE_VIEWPORT } from 'utils/constants';
import bellIcon from '../../icons/bell.svg';
import wifiIcon from '../../icons/wifi-icon.svg';
import ActiveDataRequests from './ActiveDataRequests/ActiveDataRequests';
import ClosedDataRequests from './ClosedDataRequests/ClosedDataRequests';
import Notifications from './Notifications/Notifications';
import { BsArchive } from 'react-icons/bs';
import styles from './DataRequests.module.scss';

interface StateProps {
  waitingSellerProposals: ProposalEntity[];
  waitingBuyerProposals: ProposalEntity[];
  approvedProposals: ProposalEntity[];
  closedApproved: ProposalEntity[];
  closedRejected: ProposalEntity[];
  closedDeclined: ProposalEntity[];
  closedCancelled: ProposalEntity[];
}

const DataRequests = ({
  waitingSellerProposals,
  waitingBuyerProposals,
  approvedProposals,
  closedApproved,
  closedRejected,
  closedDeclined,
  closedCancelled,
}: StateProps) => {
  const [displayBottomImage, setDisplayBottomImage] = useState(
    window.innerWidth > MOBILE_VIEWPORT ? true : false
  );
  const { path, url } = useRouteMatch();
  const { unreadNotifications, refreshUnreadotifications } =
    useNotificationContext();

  const LINKS = [
    {
      to: `${url}/active/waiting-seller`,
      label: 'Active Requests',
      key: 'active',
      icon: wifiIcon,
      count: waitingSellerProposals?.length || 0,
    },
    {
      to: `${url}/closed/approved`,
      label: 'Closed Requests',
      key: 'closed',
      icon: BsArchive,
      count: null,
    },
    {
      to: `${url}/notifications`,
      label: 'Notifications',
      key: 'notifications',
      icon: bellIcon,
      count: unreadNotifications?.count,
    },
  ];

  if (url === '/provider') {
    return <Redirect to="/data-requests/active/waiting-seller" />;
  }

  window.addEventListener('resize', () => {
    setDisplayBottomImage(window.innerWidth > MOBILE_VIEWPORT ? true : false);
  });

  return (
    <div className={classnames('mt-0 bg-white h-100')}>
      <BackgroundImages
        showTopImage={false}
        showBottomImage={displayBottomImage}
        bottomImage={BG_IMAGES.bottomRightYellowBgImage}
      />
      <div
        className={classnames(
          'd-flex',
          'justify-content-between',
          'bg-light',
          'position-relative',
          'z-index-1',
          'page-title',
          styles.headerContainer
        )}
      >
        <div className="container-xl d-flex justify-content-between position-relative fix-bg-images">
          <div className="d-flex align-items-center text-truncate">
            <h1
              className={classnames(
                'm-0',
                'text-truncate',
                'pr-1',
                styles.header
              )}
            >
              Data Requests
            </h1>
          </div>
          <div className={classnames('d-none d-lg-flex align-items-center')}>
            {LINKS.map((link, index) => {
              const Icon = link.icon;
              return (
                <NavLink
                  key={index}
                  to={link.to}
                  className="subNavLink text-right"
                  isActive={(_, location) =>
                    location.pathname.includes(link.key)
                  }
                >
                  <Badge
                    pill
                    className="ml-2 ml-lg-5 d-flex align-items-center"
                  >
                    {typeof Icon === 'string' ? (
                      <img src={Icon} className="mr-2" alt="navIcon"></img>
                    ) : (
                      <Icon className="mr-2" size={25} />
                    )}

                    {link.label}
                    {link.count ? (
                      <Count className="ml-2" value={link.count} />
                    ) : null}
                  </Badge>
                </NavLink>
              );
            })}
          </div>
        </div>
      </div>
      <div className={classnames('bg-white h-100 pt-2 pt-lg-5 px-0 px-lg-4')}>
        <Switch>
          <Route path={`${path}/active`}>
            <ActiveDataRequests
              waitingSellerProposals={waitingSellerProposals}
              waitingBuyerProposals={waitingBuyerProposals}
              approvedProposals={approvedProposals}
            />
          </Route>
          <Route path={`${path}/closed`}>
            <ClosedDataRequests
              closedApproved={closedApproved}
              closedRejected={closedRejected}
              closedDeclined={closedDeclined}
              closedCancelled={closedCancelled}
            />
          </Route>
          <Route path={`${path}/notifications`}>
            <Notifications
              unreadNotifications={unreadNotifications.results}
              refreshUnreadotifications={refreshUnreadotifications}
            />
          </Route>
        </Switch>
      </div>
    </div>
  );
};

const mapStateToProps = (state: GenericObject) => {
  return (
    state.proposals.multipleProposals as ProposalEntity[]
  ).reduce<StateProps>(
    (acc, proposal) => {
      let filtered = {};

      if (isWaitingForSellerProposal(proposal)) {
        filtered = {
          waitingSellerProposals: [...acc.waitingSellerProposals, proposal],
        };
      } else if (isWaitingBuyerProposal(proposal)) {
        filtered = {
          waitingBuyerProposals: [...acc.waitingBuyerProposals, proposal],
        };
      } else if (isApprovedProposal(proposal)) {
        filtered = { approvedProposals: [...acc.approvedProposals, proposal] };
      } else if (isClosedApprovedProposal(proposal)) {
        filtered = { closedApproved: [...acc.closedApproved, proposal] };
      } else if (isClosedRejectedProposal(proposal)) {
        filtered = { closedRejected: [...acc.closedRejected, proposal] };
      } else if (isClosedDeclinedProposal(proposal)) {
        filtered = { closedDeclined: [...acc.closedDeclined, proposal] };
      } else if (isClosedCancelledProposal(proposal)) {
        filtered = { closedCancelled: [...acc.closedCancelled, proposal] };
      }

      return { ...acc, ...filtered };
    },
    {
      waitingSellerProposals: [],
      waitingBuyerProposals: [],
      approvedProposals: [],
      closedApproved: [],
      closedRejected: [],
      closedDeclined: [],
      closedCancelled: [],
    }
  );
};

export default connect<StateProps>(mapStateToProps)(DataRequests);
